import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import ConfigurationsContext from "context/configurations/ConfigurationsContext";
import { useGetUserDetailsQuery } from "features/api/userApi";
import type { RootState } from "stores/application.store";
import { useGetActiveMessagesQuery } from "./messagesApi";
import { pageForPath } from "./types";

export function useActiveMessages() {
	const { pathname } = useLocation();
	const { configurationsData } = useContext(ConfigurationsContext);
	const enabled = configurationsData?.featureFlags?.bannersAndAdsEnabled === true;
	const { data: user } = useGetUserDetailsQuery();
	const impersonation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const contextKey = JSON.stringify([user?.email, impersonation?.email, impersonation?.accountId]);
	const result = useGetActiveMessagesQuery(
		{ page: pageForPath(pathname), contextKey },
		{
			skip: !enabled,
			pollingInterval: 60_000,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true
		}
	);
	const { refetch, currentData } = result;
	useEffect(() => {
		if (enabled) refetch();
	}, [enabled, pathname, refetch]);
	useEffect(() => {
		if (!enabled || !currentData?.nextChangeUtc) return;
		const timer = window.setTimeout(
			() => {
				refetch();
			},
			Math.min(2_147_483_647, Math.max(100, Date.parse(currentData.nextChangeUtc) - Date.now() + 50))
		);
		return () => window.clearTimeout(timer);
	}, [enabled, currentData?.nextChangeUtc, refetch]);
	return { ...result, enabled };
}
